import { zodResolver } from '@hookform/resolvers/zod';
import { Add, Delete } from '@mui/icons-material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Control,
  FormProvider,
  Resolver,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';
import { getHolds } from '../../../master-data';
import { StorageKey } from '../../../../shared/auth/DataService';
import { upsertStockDelta } from '../../../../shared/api/scenarioApi';
import { useScenario } from '../../../../shared/contexts/ScenarioContext';
import useScenarioMode from '../../../../shared/hooks/useScenarioMode';
import MBModal from '../../../../shared/components/modals/MBModal';
import RHFAutocomplete from '../../../../shared/components/react-hook-form/RHFAutocomplete';
import RHFNumber from '../../../../shared/components/react-hook-form/RHFNumber';
import {
  useReportingFilters,
  useReportingCoordination,
} from '../../../vessel/store';
import { useParamsBuilder } from '../../../vessel/hooks/useVesselUtils';
import { useDateFormatter } from '../../../../shared/hooks/useDateFormatter';
import {
  addStock,
  getStockCombinationBreakdown,
} from '../../services/StockRepository';
import { buildStockPatch } from '../../utils/buildStockPatch';
import type { RHFStockFormValues } from './RHFStockForm';

// ── Types ─────────────────────────────────────────────────────

interface StockCombinationBreakdown {
  stockId: string;
  marketId: number;
  marketName?: string | null;
  marketCountryCode?: string | null;
  portOperatorId: number;
  portOperatorName?: string | null;
  departurePortUnLocode: string;
  destinationPortUnLocode: string;
  currentStock: number | null;
  actualInflow: number | null;
  forecastInflow: number | null;
  totalBlockedCars: number | null;
  holds: { holdId: number; holdName?: string | null; volume: number }[];
}

/** Metadata about one aggregated row — originals needed for delta-based save. */
interface AggregationMeta {
  originalCurrentStock: number;
  originalActualInflow: number;
  originalForecastInflow: number;
  originalHolds: { hold: string; volume: number }[];
  underlyingRecords: StockCombinationBreakdown[];
  baselineRecord: StockCombinationBreakdown;
}

interface StockQuickEditModalProps {
  open: boolean;
  date: string | null;
  onClose: () => void;
  onSaved?: () => void;
}

// ── Validation (mirrors RHFStockForm's numeric/holds schema) ───

const holdSchema = z.object({
  hold: z
    .string()
    .nullable()
    .refine((v) => v !== null && v.length > 0, {
      message: 'Hold is required',
    }),
  volume: z.preprocess(
    (v) => (typeof v === 'string' ? Number(v || 0) : v),
    z.number()
  ),
  // TODO: re-enable once negative-volume handling is finalized
  // .refine((v) => v >= 0, { message: 'Volume must be >= 0' }),
});

const combinationSchema = z.object({
  aggregatedId: z.string(),
  stockId: z.string(),
  marketId: z.number(),
  marketName: z.string().nullable().optional(),
  portOperatorId: z.number(),
  portOperatorName: z.string().nullable().optional(),
  departurePortUnLocode: z.string(),
  destinationPortUnLocode: z.string(),
  currentStock: z.preprocess(
    (v) => (typeof v === 'string' ? Number(v || 0) : v),
    z.number()
  ),
  // TODO: re-enable once negative-value handling is finalized
  // .refine((v) => v >= 0, { message: 'Current Stock must be >= 0' }),
  actualInflow: z.preprocess(
    (v) => (typeof v === 'string' ? Number(v || 0) : v),
    z.number()
  ),
  // .refine((v) => v >= 0, { message: 'Actual Inflow must be >= 0' }),
  forecastInflow: z.preprocess(
    (v) => (typeof v === 'string' ? Number(v || 0) : v),
    z.number()
  ),
  // .refine((v) => v >= 0, { message: 'Forecast Inflow must be >= 0' }),
  holds: z.array(holdSchema),
});

const formSchema = z.object({
  combinations: z.array(combinationSchema),
});

type FormValues = z.infer<typeof formSchema>;
type CombinationValues = FormValues['combinations'][number];

// ── Helpers ───────────────────────────────────────────────────

const totalBlockedCars = (holds: CombinationValues['holds']) =>
  (holds ?? []).reduce((sum, h) => sum + (Number(h.volume) || 0), 0);

/**
 * Aggregates breakdown records that share the same country code (plus port
 * operator / route dimensions). Returns form-ready rows and a metadata map
 * that stores original aggregated totals needed for delta-based saving.
 */
const aggregateByCountryCode = (
  records: StockCombinationBreakdown[]
): {
  formCombinations: FormValues['combinations'];
  metaMap: Record<string, AggregationMeta>;
} => {
  const groups: Record<string, StockCombinationBreakdown[]> = {};

  for (const r of records) {
    const cc = r.marketCountryCode;
    // Records without a country code stay as their own row (no aggregation).
    const key = cc
      ? `agg_${cc}_${r.portOperatorId}_${r.departurePortUnLocode}_${r.destinationPortUnLocode}`
      : `single_${r.stockId}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  }

  const formCombinations: FormValues['combinations'] = [];
  const metaMap: Record<string, AggregationMeta> = {};

  for (const [aggId, group] of Object.entries(groups)) {
    // Baseline = record with the smallest marketId
    const sorted = [...group].sort((a, b) => a.marketId - b.marketId);
    const baseline = sorted[0];

    const totalCS = sorted.reduce((s, r) => s + (r.currentStock ?? 0), 0);
    const totalAI = sorted.reduce((s, r) => s + (r.actualInflow ?? 0), 0);
    const totalFI = sorted.reduce((s, r) => s + (r.forecastInflow ?? 0), 0);

    // Merge holds by holdId — sum volumes for the same hold across records
    const holdMap: Record<string, number> = {};
    for (const r of sorted) {
      for (const h of r.holds ?? []) {
        const hKey = String(h.holdId);
        holdMap[hKey] = (holdMap[hKey] ?? 0) + (h.volume ?? 0);
      }
    }
    const mergedHolds = Object.entries(holdMap).map(([hold, volume]) => ({
      hold,
      volume,
    }));

    formCombinations.push({
      aggregatedId: aggId,
      stockId: baseline.stockId,
      marketId: baseline.marketId,
      marketName: baseline.marketName,
      portOperatorId: baseline.portOperatorId,
      portOperatorName: baseline.portOperatorName,
      departurePortUnLocode: baseline.departurePortUnLocode,
      destinationPortUnLocode: baseline.destinationPortUnLocode,
      currentStock: totalCS,
      actualInflow: totalAI,
      forecastInflow: totalFI,
      holds: mergedHolds,
    });

    metaMap[aggId] = {
      originalCurrentStock: totalCS,
      originalActualInflow: totalAI,
      originalForecastInflow: totalFI,
      originalHolds: mergedHolds.map((h) => ({ ...h })),
      underlyingRecords: sorted,
      baselineRecord: baseline,
    };
  }

  return { formCombinations, metaMap };
};

/**
 * Builds the flat StockDTO payload expected by POST /stock/with-holds
 * (non-scenario write path) — mirrors what RHFStockForm.onSubmit produces.
 */
const buildFlatStockPayload = (
  values: RHFStockFormValues,
  updatedBy: string | null
) => ({
  market: Number(values.market),
  portOperator: Number(values.portOperator),
  departurePort: values.departurePort,
  destinationPort: values.destinationPort,
  date: values.date,
  actualInflow: values.actualInflow,
  forecastInflow: values.forecastInflow,
  currentStock: values.currentStock,
  holds: (values.holds ?? []).map((h) => ({
    hold: h.hold != null ? Number(h.hold) : null,
    volume: h.volume,
    ...(updatedBy ? { updatedBy } : {}),
  })),
  ...(updatedBy ? { updatedBy } : {}),
});

// ── Detail panel: per-hold editor for a single combination row ─

interface CombinationHoldsEditorProps {
  index: number;
  control: Control<any>;
  holdOptions: { label: string; value: string }[];
}

const CombinationHoldsEditor = ({
  index,
  control,
  holdOptions,
}: CombinationHoldsEditorProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `combinations.${index}.holds`,
  });

  const watchedHolds = useWatch({
    control,
    name: `combinations.${index}.holds`,
  });
  const selectedHoldValues = useMemo(
    () =>
      (watchedHolds ?? [])
        .map((h: { hold?: string | null }) => h?.hold)
        .filter(Boolean) as string[],
    [watchedHolds]
  );

  return (
    <Box
      sx={{
        p: 2,
        // Opaque background so this panel isn't visually tinted by the
        // parent row's dirty-state highlight (which sits behind it).
        backgroundColor: 'background.paper',
      }}
    >
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ mb: 1, display: 'block' }}
      >
        Blocked Cars by Hold
      </Typography>
      <Stack spacing={1}>
        {fields.map((field, holdIndex) => {
          const currentValue = selectedHoldValues[holdIndex] ?? null;
          const otherSelected = selectedHoldValues.filter(
            (_, i) => i !== holdIndex
          );
          const filteredOptions = holdOptions.filter(
            (opt) =>
              opt.value === currentValue || !otherSelected.includes(opt.value)
          );

          return (
            <Stack
              key={field.id}
              direction='row'
              spacing={1}
              alignItems='center'
            >
              <Box sx={{ flex: 1, maxWidth: 280 }}>
                <RHFAutocomplete
                  name={`combinations.${index}.holds.${holdIndex}.hold`}
                  options={filteredOptions}
                  label='Hold'
                  required
                  size='small'
                />
              </Box>
              <Box sx={{ width: 120 }}>
                <RHFNumber
                  name={`combinations.${index}.holds.${holdIndex}.volume`}
                  label='Volume'
                  size='small'
                />
              </Box>
              <IconButton
                size='small'
                color='error'
                onClick={() => remove(holdIndex)}
              >
                <Delete fontSize='small' />
              </IconButton>
            </Stack>
          );
        })}
        <Button
          size='small'
          startIcon={<Add fontSize='small' />}
          onClick={() => append({ hold: null, volume: 0 })}
          sx={{ alignSelf: 'flex-start' }}
        >
          Add Hold
        </Button>
      </Stack>
    </Box>
  );
};

// ── Main component ───────────────────────────────────────────

/**
 * Quick Edit modal for the 4 editable StockTable metrics (Current Stock,
 * Actual Inflow, Forecast Inflow, Total Blocked Cars). Resolves the real
 * Stock combination(s) behind the clicked date via
 * GET /stock/aggregatedStock/breakdown and shows them all in a single
 * DataGridPro table (no separate chooser step). Each row's hold breakdown
 * lives in a detail panel — collapsed by default, expand via the row's
 * built-in toggle when you want to view/add holds. A single "Save All"
 * writes only the rows react-hook-form marks dirty, through the existing
 * full-patch endpoints (upsertStockDelta / POST /stock/with-holds).
 */
const StockQuickEditModal = ({
  open,
  date,
  onClose,
  onSaved,
}: StockQuickEditModalProps) => {
  const [loading, setLoading] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [holdOptions, setHoldOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const apiRef = useGridApiRef();
  // Baseline snapshot from the last successful load/save — used to keep only
  // failed rows dirty after a partial Save All failure.
  const baselineRef = useRef<FormValues>({ combinations: [] });
  // Aggregation metadata — original totals & underlying records per row.
  const aggregationMetaRef = useRef<Record<string, AggregationMeta>>({});
  const holdsRequestedRef = useRef(false);

  const { filters } = useReportingFilters();
  const { fetchAllData } = useReportingCoordination();
  const formatDate = useDateFormatter();
  const { activeScenario } = useScenario();
  const { isScenarioActive } = useScenarioMode();
  const buildParams = useParamsBuilder(filters, formatDate, activeScenario);

  const dateKey = date ? String(date).split('T')[0] : null;

  const methods = useForm<FormValues>({
    defaultValues: { combinations: [] },
    resolver: zodResolver(formSchema) as Resolver<FormValues>,
    mode: 'onChange',
  });
  const { control, reset, getValues, formState, trigger } = methods;

  const watchedCombinations = useWatch({ control, name: 'combinations' });
  const combinations = useMemo(
    () => watchedCombinations ?? [],
    [watchedCombinations]
  );
  const dirtyRowIndices = useMemo(
    () =>
      combinations
        .map((_, i) => i)
        .filter((i) => Boolean(formState.dirtyFields.combinations?.[i])),
    [combinations, formState.dirtyFields]
  );

  useEffect(() => {
    if (!open || holdsRequestedRef.current) return;
    holdsRequestedRef.current = true;
    getHolds()
      .then((data: any[]) => {
        setHoldOptions(
          (data ?? []).map((h) => ({ label: h.name, value: String(h.id) }))
        );
      })
      .catch(() => {
        holdsRequestedRef.current = false;
        setHoldOptions([]);
      });
  }, [open]);

  useEffect(() => {
    if (!open || !dateKey) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setRowErrors({});
    reset({ combinations: [] });

    const params = new URLSearchParams(buildParams());
    params.set('date', dateKey);
    params.delete('startDateETD');
    params.delete('endDateETD');
    params.delete('startDateETA');
    params.delete('endDateETA');
    params.delete('groupBy');

    getStockCombinationBreakdown(params)
      .then((result: StockCombinationBreakdown[]) => {
        if (cancelled) return;
        const { formCombinations, metaMap } = aggregateByCountryCode(
          result ?? []
        );
        const nextValues: FormValues = { combinations: formCombinations };
        baselineRef.current = nextValues;
        aggregationMetaRef.current = metaMap;
        reset(nextValues);
      })
      .catch((err: any) => {
        if (!cancelled)
          setLoadError(err?.message || 'Failed to load stock breakdown.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dateKey]);

  const rowIndexByAggId = useMemo(() => {
    const map: Record<string, number> = {};
    combinations.forEach((c, i) => {
      map[c.aggregatedId] = i;
    });
    return map;
  }, [combinations]);

  const saveRow = async (row: CombinationValues) => {
    const dateIso = dayjs(date).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
    const meta = aggregationMetaRef.current[row.aggregatedId];

    let saveMarketId = row.marketId;
    let saveCurrentStock = row.currentStock;
    let saveActualInflow = row.actualInflow;
    let saveForecastInflow = row.forecastInflow;
    let saveHolds = row.holds;

    // For aggregated rows (multiple underlying markets) compute deltas and
    // apply them to the baseline record (smallest marketId).
    if (meta && meta.underlyingRecords.length > 1) {
      const baseline = meta.baselineRecord;
      saveMarketId = baseline.marketId;

      const deltaCS = row.currentStock - meta.originalCurrentStock;
      const deltaAI = row.actualInflow - meta.originalActualInflow;
      const deltaFI = row.forecastInflow - meta.originalForecastInflow;

      saveCurrentStock = (baseline.currentStock ?? 0) + deltaCS;
      saveActualInflow = (baseline.actualInflow ?? 0) + deltaAI;
      saveForecastInflow = (baseline.forecastInflow ?? 0) + deltaFI;

      // Holds delta: apply per-hold deltas to the baseline record's holds.
      const baselineHoldsMap: Record<string, number> = {};
      for (const h of baseline.holds ?? []) {
        baselineHoldsMap[String(h.holdId)] = h.volume ?? 0;
      }
      const originalAggHoldsMap: Record<string, number> = {};
      for (const h of meta.originalHolds) {
        originalAggHoldsMap[h.hold] = h.volume;
      }

      saveHolds = row.holds.map((h) => {
        const holdKey = h.hold ?? '';
        const originalAggVol = originalAggHoldsMap[holdKey] ?? 0;
        const baselineVol = baselineHoldsMap[holdKey] ?? 0;
        const deltaVol = (Number(h.volume) || 0) - originalAggVol;
        return { hold: h.hold, volume: baselineVol + deltaVol };
      });
    }

    const values: RHFStockFormValues = {
      market: String(saveMarketId),
      portOperator: String(row.portOperatorId),
      departurePort: row.departurePortUnLocode,
      destinationPort: row.destinationPortUnLocode,
      date: dateIso,
      actualInflow: saveActualInflow,
      forecastInflow: saveForecastInflow,
      currentStock: saveCurrentStock,
      holds: saveHolds,
    };

    if (isScenarioActive && activeScenario) {
      await upsertStockDelta(activeScenario.id, {
        changeType: 'MODIFIED',
        patch: buildStockPatch(values),
      });
    } else {
      const emea = localStorage.getItem(StorageKey.userInfo_sub);
      await addStock(buildFlatStockPayload(values, emea));
    }
  };

  const handleSaveAll = async () => {
    if (dirtyRowIndices.length === 0) {
      onClose();
      return;
    }

    const valid = await trigger();
    if (!valid) return;

    setSavingAll(true);
    const values = getValues('combinations') as CombinationValues[];
    const dirtyRows = dirtyRowIndices.map((i) => values[i]);

    const results = await Promise.allSettled(
      dirtyRows.map((row) => saveRow(row))
    );

    const succeededAggIds = new Set<string>();
    const nextErrors: Record<string, string> = {};
    results.forEach((result, i) => {
      const row = dirtyRows[i];
      if (result.status === 'fulfilled') {
        succeededAggIds.add(row.aggregatedId);
      } else {
        nextErrors[row.aggregatedId] =
          (result.reason as any)?.message || 'Failed to save this combination.';
      }
    });
    setRowErrors(nextErrors);
    setSavingAll(false);

    const anyFailed = Object.keys(nextErrors).length > 0;
    if (!anyFailed) {
      fetchAllData();
      onSaved?.();
      onClose();
      return;
    }

    // Partial failure: re-baseline succeeded rows so they're no longer dirty,
    // but keep failed rows' baseline unchanged so they remain dirty and can be
    // retried (their current in-progress edits are preserved via keepValues).
    const nextBaseline: FormValues = {
      combinations: values.map((v, i) =>
        succeededAggIds.has(v.aggregatedId)
          ? v
          : baselineRef.current.combinations[i]
      ),
    };
    baselineRef.current = nextBaseline;
    reset(nextBaseline, { keepValues: true });
  };

  const dirtyCount = dirtyRowIndices.length;

  const columns: GridColDef<CombinationValues>[] = useMemo(
    () => [
      {
        field: 'marketName',
        headerName: 'Market',
        flex: 1,
        minWidth: 140,
        valueGetter: (_v, row) => row.marketName ?? row.marketId,
      },
      {
        field: 'route',
        headerName: 'Route',
        flex: 1,
        minWidth: 160,
        valueGetter: (_v, row) =>
          `${row.departurePortUnLocode} → ${row.destinationPortUnLocode}`,
      },
      {
        field: 'portOperatorName',
        headerName: 'Port Operator',
        flex: 1,
        minWidth: 140,
        valueGetter: (_v, row) => row.portOperatorName ?? row.portOperatorId,
      },
      {
        field: 'currentStock',
        headerName: 'Current Stock',
        width: 150,
        renderCell: (params: GridRenderCellParams<CombinationValues>) => {
          const index = rowIndexByAggId[params.row.aggregatedId];
          if (index == null) return null;
          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <RHFNumber
                name={`combinations.${index}.currentStock`}
                size='small'
                fullWidth
                helperText=''
                FormHelperTextProps={{ sx: { display: 'none' } }}
              />
            </Box>
          );
        },
      },
      {
        field: 'actualInflow',
        headerName: 'Actual Inflow',
        width: 150,
        renderCell: (params: GridRenderCellParams<CombinationValues>) => {
          const index = rowIndexByAggId[params.row.aggregatedId];
          if (index == null) return null;
          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <RHFNumber
                name={`combinations.${index}.actualInflow`}
                size='small'
                fullWidth
                helperText=''
                FormHelperTextProps={{ sx: { display: 'none' } }}
              />
            </Box>
          );
        },
      },
      {
        field: 'forecastInflow',
        headerName: 'Forecast Inflow',
        width: 150,
        renderCell: (params: GridRenderCellParams<CombinationValues>) => {
          const index = rowIndexByAggId[params.row.aggregatedId];
          if (index == null) return null;
          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <RHFNumber
                name={`combinations.${index}.forecastInflow`}
                size='small'
                fullWidth
                helperText=''
                FormHelperTextProps={{ sx: { display: 'none' } }}
              />
            </Box>
          );
        },
      },
      {
        field: 'totalBlockedCars',
        headerName: 'Total Blocked Cars',
        width: 200,
        renderCell: (params: GridRenderCellParams<CombinationValues>) => {
          const error = rowErrors[params.row.aggregatedId];
          return (
            <Stack
              direction='row'
              spacing={0.5}
              alignItems='center'
              sx={{ width: '100%', height: '100%' }}
            >
              {error && (
                <Tooltip title={error}>
                  <WarningAmberIcon color='error' fontSize='small' />
                </Tooltip>
              )}
              <Typography variant='body2'>
                {totalBlockedCars(params.row.holds)}
              </Typography>
              <Tooltip title='Add Holdings'>
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    apiRef.current.toggleDetailPanel(params.row.aggregatedId);
                  }}
                >
                  <Add fontSize='small' />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [rowIndexByAggId, rowErrors, apiRef]
  );

  const getDetailPanelContent = ({ row }: GridRowParams<CombinationValues>) => {
    const index = rowIndexByAggId[row.aggregatedId];
    if (index == null) return null;
    return (
      <CombinationHoldsEditor
        index={index}
        control={control}
        holdOptions={holdOptions}
      />
    );
  };

  const getDetailPanelHeight = () => 'auto' as const;

  return (
    <MBModal
      open={open}
      onClose={onClose}
      disableClose={savingAll}
      maxWidth='lg'
      fullWidth
      title={
        <Stack direction='row' alignItems='center' spacing={1}>
          <span>Quick Edit Stock — {dateKey ?? ''}</span>
          {isScenarioActive && (
            <Chip
              icon={<ScienceOutlinedIcon />}
              label='Scenario Mode'
              color='warning'
              size='small'
            />
          )}
        </Stack>
      }
      actions={
        <Stack direction='row' spacing={2} sx={{ padding: '20px 24px' }}>
          <Button
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={savingAll}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSaveAll}
            disabled={
              savingAll ||
              loading ||
              (combinations.length > 0 && dirtyCount === 0)
            }
            startIcon={
              savingAll ? (
                <CircularProgress size={18} color='inherit' />
              ) : undefined
            }
          >
            {dirtyCount > 0 ? `Save All (${dirtyCount})` : 'Save All'}
          </Button>
        </Stack>
      }
    >
      <FormProvider {...methods}>
        {loading && (
          <Stack alignItems='center' py={4}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {!loading && loadError && <Alert severity='error'>{loadError}</Alert>}

        {!loading && !loadError && combinations.length === 0 && (
          <Typography variant='body2' color='text.secondary'>
            No underlying stock combination found for this date.
          </Typography>
        )}

        {!loading && !loadError && combinations.length > 0 && (
          <Box sx={{ width: '100%', height: 450 }}>
            <DataGridPro
              apiRef={apiRef}
              rows={combinations}
              columns={columns}
              getRowId={(row) => row.aggregatedId}
              getDetailPanelContent={getDetailPanelContent}
              getDetailPanelHeight={getDetailPanelHeight}
              getRowClassName={(params) =>
                dirtyRowIndices.includes(
                  rowIndexByAggId[params.row.aggregatedId]
                )
                  ? 'quick-edit-dirty-row'
                  : ''
              }
              hideFooter
              disableRowSelectionOnClick
              rowHeight={56}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '& .quick-edit-dirty-row': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 193, 7, 0.08)'
                      : 'rgba(255, 193, 7, 0.12)',
                },
              }}
            />
          </Box>
        )}
      </FormProvider>
    </MBModal>
  );
};

export default StockQuickEditModal;
